

import axios from 'axios';

/**
 * Shared Axios instance
 * - Sends X-CSRFToken for mutating requests (Django-compatible)
 * - Includes cookies (withCredentials)
 * - ETag-aware client cache for GETs (If-None-Match / 304 reuse)
 */

// In-memory cache keyed by request URL
const cache = new Map();

// Read a cookie by name (for csrftoken)
function getCookie(name) {
  const m = document.cookie.match(new RegExp('(^|; )' + name + '=([^;]+)'));
  return m ? decodeURIComponent(m[2]) : null;
}

const api = axios.create({
  baseURL: '',                 // use absolute/relative paths as-is
  withCredentials: true,       // send cookies
  timeout: 20000,
  xsrfCookieName: 'csrftoken', // Django defaults
  xsrfHeaderName: 'X-CSRFToken',
});

// REQUEST: attach CSRF for unsafe methods, ETag for cached GETs
api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();

  // Always include credentials
  config.withCredentials = true;

  // CSRF for mutating requests
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const token = getCookie('csrftoken');
    if (token) {
      config.headers = config.headers || {};
      config.headers['X-CSRFToken'] = token;
    }
  }

  // ETag: If we have a cached etag for this GET URL, send If-None-Match
  if (method === 'get') {
    const entry = cache.get(config.url);
    if (entry?.etag) {
      config.headers = config.headers || {};
      config.headers['If-None-Match'] = entry.etag;
    }
  }
  return config;
});

// RESPONSE: store ETag + data for GETs; reuse cache on 304
api.interceptors.response.use(
  (response) => {
    if ((response.config.method || 'get').toLowerCase() === 'get') {
      const etag = response.headers?.etag;
      if (etag) {
        cache.set(response.config.url, {
          etag,
          data: response.data,
          ts: Date.now(),
        });
      }
    }
    return response;
  },
  (error) => {
    const { response, config } = error || {};
    if (response && response.status === 304 && (config?.method || 'get').toLowerCase() === 'get') {
      const entry = cache.get(config.url);
      if (entry) {
        return Promise.resolve({
          ...response,
          status: 200,
          data: entry.data,
          fromCache: true,
        });
      }
    }
    return Promise.reject(error);
  }
);

export default api;